import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  LinearProgress,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import TranslateIcon from '@mui/icons-material/Translate';
import { uploadAudioFile, getAudioFiles } from '../controllers/TranscriptController';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../models/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const TranscriptView = () => {
  const [file, setFile] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [transcription, setTranscription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const baseUrl = 'https://us-central1-theapp-428906.cloudfunctions.net/transcribeAudio'; // Remplacez par votre URL de base

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    } else {
      loadAudioFiles();
    }
  }, [currentUser, navigate]);

  const loadAudioFiles = async () => {
    try {
      const files = await getAudioFiles();
      setAudioFiles(files);
    } catch (error) {
      setError('Erreur lors du chargement des fichiers audio');
      console.error(error);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
      setUploadProgress(0);
    }
  };

  const handleUpload = async (file) => {
    try {
      setIsLoading(true);
      setError('');
      const url = await uploadAudioFile(file);
      setFile({ name: file.name, url: url });
    } catch (error) {
      setError('Erreur lors de l\'upload du fichier audio: ' + error.message);
    } finally {
      setIsLoading(false);
      loadAudioFiles();
      setFile(null);
    }
  };


  const handleTranscribe = async (fileUrl) => {
    try {
      setIsLoading(true);
      setError('');
      console.info(fileUrl);
      const response = await axios.post(`https://us-central1-theapp-428906.cloudfunctions.net/transcribeAudio`, { audioUrl: fileUrl });
      setTranscription(response.data.transcription);
    } catch (error) {
      setError('Erreur lors de la transcription: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayAudio = (url) => {
    window.open(url, '_blank');
  };

  const handleDelete = (fileName) => {
    // Implémentez la fonction de suppression ici
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Transcription Audio
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            sx={{ mb: 2 }}
          >
            Sélectionner un fichier audio
            <VisuallyHiddenInput type="file" onChange={handleFileChange} />
          </Button>

          {file && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                Fichier sélectionné: {file.name}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleUpload(file)}
                disabled={isLoading}
                sx={{ mt: 1 }}
              >
                Upload
              </Button>
            </Box>
          )}

          {isLoading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
          Fichiers Audio Disponibles
        </Typography>
        <List>
          {audioFiles.map((audioFile, index) => (
            <ListItem key={index} divider>
              <ListItemText 
                primary={audioFile.name}
                secondary={`Chemin: ${audioFile.path}`}
              />
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  aria-label="play"
                  onClick={() => handlePlayAudio(audioFile.url)}
                  sx={{ mr: 1 }}
                >
                  <PlayArrowIcon />
                </IconButton>
                <IconButton 
                  edge="end" 
                  aria-label="transcribe"
                  onClick={() => handleTranscribe(audioFile.url)}
                >
                  <TranslateIcon />
                </IconButton>
                <IconButton 
                  edge="end" 
                  aria-label="delete"
                  onClick={() => handleDelete(audioFile.name)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        {transcription && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Transcription
            </Typography>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
              <ReactMarkdown>{transcription}</ReactMarkdown>
            </Paper>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default TranscriptView;
